import { CoreModule } from '@abp/ng.core';
import { GdprConfigModule } from '@volo/abp.ng.gdpr/config';
import { SettingManagementConfigModule } from '@abp/ng.setting-management/config';
import {
  HTTP_ERROR_HANDLER,
  SUPPRESS_UNSAVED_CHANGES_WARNING,
  ThemeSharedModule,
} from '@abp/ng.theme.shared';
import { APP_INITIALIZER, NgModule } from '@angular/core';
import { OAuthStorage } from 'angular-oauth2-oidc';
import { purgeLegacyTokenStorage, tokenStorage } from './shared/auth/token-storage';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { CommercialUiConfigModule, ImpersonationService } from '@volo/abp.commercial.ng.ui/config';
import { SitePlantImpersonationService } from '@/shared/auth/siteplant-impersonation.service';
import { SitePlantAuthService } from '@/shared/auth/siteplant-auth.service';
import { AuthService } from '@abp/ng.core';
import { AccountAdminConfigModule } from '@volo/abp.ng.account/admin/config';
import { AccountPublicConfigModule } from '@volo/abp.ng.account/public/config';
import { AuditLoggingConfigModule } from '@volo/abp.ng.audit-logging/config';
import { OpeniddictproConfigModule } from '@volo/abp.ng.openiddictpro/config';
import { IdentityConfigModule, LinkLoginHandler } from '@volo/abp.ng.identity/config';
import { IdentityExtensionsGuard } from '@volo/abp.ng.identity';
import { SitePlantLinkLoginHandler } from './shared/auth/link-login.handler';
import { LanguageManagementConfigModule } from '@volo/abp.ng.language-management/config';
import { registerLocale } from '@volo/abp.ng.language-management/locale';
import { defaultLocalErrorHandlerFn } from '@abp/ng.core/locale';
import { cultureNameLocaleFileMap } from './locale-map';
import { SaasConfigModule } from '@volo/abp.ng.saas/config';
import { TextTemplateManagementConfigModule } from '@volo/abp.ng.text-template-management/config';
import { ThemeLeptonXModule } from '@volosoft/abp.ng.theme.lepton-x';
import { SideMenuLayoutModule } from '@volosoft/abp.ng.theme.lepton-x/layouts';
import { environment } from '../environments/environment';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { APP_ROUTE_PROVIDER } from './route.provider';
import { USER_MENU_PROVIDER } from './user-menu.provider';
import { FeatureManagementModule } from '@abp/ng.feature-management';
import { AbpOAuthModule } from '@abp/ng.oauth';
import { REPORTS_REPORT_DESIGNER_ROUTE_PROVIDER } from './reports/designer/report-designer-route.provider';
import { JOBSITE_ROUTE_PROVIDER } from './jobsites/jobsite-route.provider';
import { SITES_SITE_ROUTE_PROVIDER } from './sites/site/providers/site-route.provider';
import { ATTRIBUTE_DEFINITIONS_ATTRIBUTE_DEFINITION_ROUTE_PROVIDER } from './attribute-definitions/attribute-definition-group-route.provider';
import { CATEGORIES_CATEGORY_ROUTE_PROVIDER } from './categories/category-route.provider';
import { MANUFACTURERS_MANUFACTURER_ROUTE_PROVIDER } from './manufacturers/manufacturer/providers/manufacturer-route.provider';
import { LOCATIONS_LOCATION_ROUTE_PROVIDER } from './locations/location-route.provider';
import { ASSETS_ASSET_ROUTE_PROVIDER } from './assets/asset-route.provider';
import { CONSUMABLES_ROUTE_PROVIDER } from './consumables/consumables-route.provider';
import { STAFFS_STAFF_ROUTE_PROVIDER } from './staffs/staff/providers/staff-route.provider';
import { INSPECTION_STATUSES_INSPECTION_STATUS_ROUTE_PROVIDER } from './inspection-statuses/inspection-status/providers/inspection-status-route.provider';
import { INSPECTION_TYPES_INSPECTION_TYPE_ROUTE_PROVIDER } from './inspection-types/inspection-types-route.provider';
import { INSPECTIONS_INSPECTION_ROUTE_PROVIDER } from './inspections/inspection-route.provider';
import { APPLICATION_SETTINGS__ROUTE_PROVIDER } from './settings/settings.provider';
import { LogoComponent } from './components/layout/logo/logo.component';
import { AppHttpErrorComponent } from './shared/http-error/http-error.component';
import { appHttpErrorHandler } from './shared/http-error/http-error.handler';
import { UserModule } from './users/user.module';
import { LpxSideMenuLayoutModule } from '@volosoft/ngx-lepton-x/layouts';
import { LpxResponsiveModule } from '@volo/ngx-lepton-x.core'; // optional. Only, if you are using lpxResponsive directive
import { LeptonXAbpCoreModule } from '@volo/abp.ng.lepton-x.core';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import { QTY_ASSETS_ROUTE_PROVIDER } from './qty-assets/qty-assets-route.provider';
import { AuthHttpInterceptor } from '@proxy/interceptors/http.interceptor';
import { SharedModule } from './shared/shared.module';
import { AppLpxMobileNavbarModule } from './components/layout/mobile/mobile-navbar.module';

import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { COST_CENTRE_ROUTE_PROVIDER } from './cost-centre/cost-centre-route.provider';
import { TenantClonePopupModule } from './tenants/tenant-clone-popup/tenant-clone-popup.module';

@NgModule({
  declarations: [AppComponent, AppHttpErrorComponent],
  imports: [
    LogoComponent,
    NgbModule,
    BrowserModule,
    BrowserAnimationsModule,
    FormsModule,
    ReactiveFormsModule,
    AppRoutingModule,
    AppLpxMobileNavbarModule,
    TenantClonePopupModule,
    CoreModule.forRoot({
      localizations: [
        {
          culture: 'en-GB',
          resources: [
            {
              resourceName: 'RegisterService',
              texts: {
                'Menu:RegisterService': 'Register',
                HomePage: 'Home',
                'Enum:EditorType:TagBox': 'Tag box',
              },
            },
          ],
        },
      ],
      environment,
      registerLocaleFn: registerLocale({
        cultureNameLocaleFileMap,
        errorHandlerFn: defaultLocalErrorHandlerFn,
      }),
    }),
    AbpOAuthModule.forRoot(),
    ThemeSharedModule.forRoot({
      httpErrorConfig: {
        errorScreen: {
          component: AppHttpErrorComponent,
          forWhichErrors: [401, 403, 404, 500],
          hideCloseIcon: true,
        },
      },
    }),
    AccountAdminConfigModule.forRoot(),
    AccountPublicConfigModule.forRoot(),
    IdentityConfigModule.forRoot(),
    LanguageManagementConfigModule.forRoot(),
    SaasConfigModule.forRoot(),
    AuditLoggingConfigModule.forRoot(),
    OpeniddictproConfigModule.forRoot(),
    TextTemplateManagementConfigModule.forRoot(),
    SettingManagementConfigModule.forRoot(),
    ThemeLeptonXModule.forRoot(),
    SideMenuLayoutModule.forRoot(),
    CommercialUiConfigModule.forRoot(),
    FeatureManagementModule.forRoot(),
    GdprConfigModule.forRoot({
      privacyPolicyUrl: 'gdpr-cookie-consent/privacy',
      cookiePolicyUrl: 'gdpr-cookie-consent/cookie',
    }),
    UserModule,
    LpxSideMenuLayoutModule,
    LpxResponsiveModule,
    LeptonXAbpCoreModule,
    SharedModule,
  ],
  providers: [
    { provide: HTTP_ERROR_HANDLER, useValue: appHttpErrorHandler },
    { provide: SUPPRESS_UNSAVED_CHANGES_WARNING, useValue: true },
    APP_ROUTE_PROVIDER,
    USER_MENU_PROVIDER,
    APPLICATION_SETTINGS__ROUTE_PROVIDER,
    SITES_SITE_ROUTE_PROVIDER,
    CONSUMABLES_ROUTE_PROVIDER,
    ATTRIBUTE_DEFINITIONS_ATTRIBUTE_DEFINITION_ROUTE_PROVIDER,
    CATEGORIES_CATEGORY_ROUTE_PROVIDER,
    MANUFACTURERS_MANUFACTURER_ROUTE_PROVIDER,
    QTY_ASSETS_ROUTE_PROVIDER,
    LOCATIONS_LOCATION_ROUTE_PROVIDER,
    ASSETS_ASSET_ROUTE_PROVIDER,
    STAFFS_STAFF_ROUTE_PROVIDER,
    REPORTS_REPORT_DESIGNER_ROUTE_PROVIDER,
    INSPECTION_STATUSES_INSPECTION_STATUS_ROUTE_PROVIDER,
    INSPECTION_TYPES_INSPECTION_TYPE_ROUTE_PROVIDER,
    INSPECTIONS_INSPECTION_ROUTE_PROVIDER,
    JOBSITE_ROUTE_PROVIDER,
    COST_CENTRE_ROUTE_PROVIDER,
    IdentityExtensionsGuard,
    { provide: HTTP_INTERCEPTORS, useClass: AuthHttpInterceptor, multi: true },
    { provide: ImpersonationService, useClass: SitePlantImpersonationService },
    { provide: OAuthStorage, useValue: tokenStorage },
    { provide: LinkLoginHandler, useClass: SitePlantLinkLoginHandler },
    { provide: AuthService, useClass: SitePlantAuthService },
    {
      provide: APP_INITIALIZER,
      multi: true,
      useValue: () => purgeLegacyTokenStorage(),
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule {}
